import { reportPlatformError } from "$root/api/error-reporting";
import { portfolioLabAdapter, PortfolioLabContext } from "$root/functional-areas/portfolio-lab/ctx";
import { portfolioLabRepository } from "$root/functional-areas/portfolio-lab/repository";
import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { useCallback } from "react";

export function useLabSession() {
	const ctx = useMandatoryContext(PortfolioLabContext);

	const saveLabSession = useCallback(() => {
		// Its own lane: view-config saves are unordered relative to virtual-investment writes, so
		// they run on their own last-write-wins lane (`sessionSaveChain`). The write closure reads every
		// atom LIVE when the chain runs it — never a snapshot captured at trigger time — so a save that
		// fires behind an in-flight one always pushes the freshest editor state.
		ctx.sessionSaveChain.run(async () => {
			const payload = portfolioLabAdapter.labSession.toUpstream({
				// `toUpstream` ignores `investments` (view-config only), so no need to read `investments$`.
				investments: [],
				charts: readAtom(ctx.charts$),
				parametrizedConcepts: readAtom(ctx.parametrizedConcepts$),
				pageSplit: readAtom(ctx.pageSplit$),
				decimals: readAtom(ctx.decimals$),
				grid: {
					groupBy: readAtom(ctx.compositionGroupBy$),
					columns: readAtom(ctx.compositionColumnState$) ?? [],
					projection: readAtom(ctx.compositionProjection$),
				},
			});
			try {
				await portfolioLabRepository.updateLabSession(payload);
			} catch (error) {
				// PUT-failure: no reconciliation — the client stays authoritative and the next successful
				// save catches the server up. Report for telemetry only; no user interruption, no rollback.
				reportPlatformError(error, "ERROR", "portfolio", { message: "Unable to save the lab session view config" });
			}
		});
		// Depend on the individual (stable) atom/chain refs, NOT on `ctx` — the provider's context value is
		// a fresh object literal each render, so `[ctx]` would churn this callback's identity on every
		// provider re-render (e.g. an undo-history change) and tear down/recreate the debounced save
		// wrappers that memoize on it (`useDebounced`/`debounce` in the editor + chart widgets), flushing
		// prematurely or dropping the trailing save. The sub-atom refs are created once and stay stable.
	}, [
		ctx.charts$,
		ctx.parametrizedConcepts$,
		ctx.pageSplit$,
		ctx.decimals$,
		ctx.compositionGroupBy$,
		ctx.compositionColumnState$,
		ctx.compositionProjection$,
		ctx.sessionSaveChain,
	]);

	return { saveLabSession };
}
